import { createSlice } from "@reduxjs/toolkit";

export interface CaseConverterState {
  inputText: string;
  selectedCase: string;
  outputText: string;
}

const initialState: CaseConverterState = {
  inputText: "",
  selectedCase: "sentence",
  outputText: "",
};

export const caseConverterSlice = createSlice({
  name: "caseConverter",
  initialState,
  reducers: {
    setInputText: (state, action) => {
      state.inputText = action.payload;
    },
    setSelectedCase: (state, action) => {
      state.selectedCase = action.payload;
    },
    setOutputText: (state, action) => {
      state.outputText = action.payload;
    },
    convertCase: (state) => {
      const { inputText, selectedCase } = state;
      
      if (!inputText.trim()) {
        state.outputText = "";
        return;
      }

      let result = "";
      
      // Helper function to normalize text by splitting on various separators
      const normalizeText = (text: string) => {
        return text
          .toLowerCase()
          .replace(/[^a-zA-Z0-9\s_-]/g, "") // Remove special chars except spaces, underscores, hyphens
          .split(/[\s_-]+/) // Split on spaces, underscores, or hyphens
          .filter(word => word.length > 0); // Remove empty strings
      };
      
      switch (selectedCase) {
        case "sentence":
          result = inputText.toLowerCase().replace(/(^\w|\.\s+\w)/g, (letter) => letter.toUpperCase());
          break;
        case "lower":
          result = inputText.toLowerCase();
          break;
        case "upper":
          result = inputText.toUpperCase();
          break;
        case "pascal":
          result = normalizeText(inputText)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join("");
          break;
        case "camel":
          result = normalizeText(inputText)
            .map((word, index) => 
              index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
            )
            .join("");
          break;
        case "title":
          result = normalizeText(inputText)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
          break;
        case "kebab":
          result = normalizeText(inputText).join("-");
          break;
        case "snake":
          result = normalizeText(inputText).join("_");
          break;
        default:
          result = inputText;
      }
      
      state.outputText = result;
    },
    clearAll: (state) => {
      state.inputText = "";
      state.outputText = "";
    },
  },
});

export const { setInputText, setSelectedCase, setOutputText, convertCase, clearAll } = caseConverterSlice.actions;

export const selectState = (state: { caseConverter: CaseConverterState }) => state.caseConverter;

export default caseConverterSlice.reducer;
